import { SignIn, SignUp } from '@clerk/react';
import { useState, type ReactNode } from 'react';
import { API, apiRequest, jsonPost } from '../services/api-client';
import { signInToLocalPreview } from '../services/local-preview-auth';
import { PublicShell } from '../public/PublicShell';
import { useCommerce } from './commerce';
import { safeReturnTo } from './return-to';
import { TERMS_VERSION } from '../../../shared/account-policy';

export function AccountGate({ children, create = false }: { children: ReactNode; create?: boolean }) {
  const commerce = useCommerce();
  if (commerce?.status === 'local') return children;
  if (!commerce || commerce.status === 'loading') return <PublicShell><p role="status">Checking account access…</p></PublicShell>;
  if (commerce.status === 'error') return <PublicShell><h1>Account access is unavailable</h1><p role="alert">{commerce.error}</p><button onClick={() => void commerce.refresh()}>Try again</button></PublicShell>;
  if (commerce.status === 'signed-out') {
    const destination = encodeURIComponent(safeReturnTo(location.pathname + location.search));
    return <PublicShell><section className="access-card"><span className="eyebrow">Your private account</span><h1>Sign in to continue</h1>
      <p>Create an account to open trace files and make videos. Your trial includes three videos over seven days, with no card required.</p>
      <a className="site-button" href={`/sign-up?returnTo=${destination}`}>Create account</a> <a href={`/sign-in?returnTo=${destination}`}>Sign in</a></section></PublicShell>;
  }
  if (!commerce.entitlement?.initialized) return <AccountSetup />;
  if (create && !commerce.entitlement.canImport) return <PublicShell><h1>{commerce.entitlement.suspended ? 'Account suspended' : 'Continue making videos'}</h1>
    <p>{commerce.entitlement.suspended ? 'New imports and renders are unavailable while your account is suspended.' : 'Your trial or paid access has ended. Choose a subscription to import traces and make videos.'}</p>
    <a className="site-button" href="/account">Account and billing</a> <a href="/videos">Your videos</a></PublicShell>;
  return children;
}

function AccountSetup() {
  const commerce = useCommerce();
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const initialize = async () => {
    setBusy(true); setError('');
    try { await apiRequest(`${API}/account/initialize`, jsonPost({ termsVersion: TERMS_VERSION, accepted })); await commerce?.refresh(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Account setup is unavailable.'); }
    finally { setBusy(false); }
  };
  return <PublicShell><section className="access-card"><h1>Finish account setup</h1><p>Your seven-day trial runs from verified signup and includes three videos, each up to 90 seconds. No card is required.</p>
    <p>Read the <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener">Terms of Use</a>. These policies are review drafts.</p>
    <label className="terms-check"><input type="checkbox" checked={accepted} onChange={event => setAccepted(event.target.checked)} /> I accept the Terms of Use ({TERMS_VERSION}).</label>
    <button disabled={!accepted || busy} onClick={() => void initialize()}>{busy ? 'Setting up your account…' : 'Finish setup'}</button>
    {error && <p role="alert">{error}</p>}</section></PublicShell>;
}

export function SignInPage() {
  const commerce = useCommerce();
  const destination = safeReturnTo(new URLSearchParams(location.search).get('returnTo'));
  const [error, setError] = useState('');
  const localLogin = async () => {
    try { await signInToLocalPreview(); location.assign(destination); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Sign-in failed.'); }
  };
  return <PublicShell><section className="access-card"><h1>{location.pathname === '/sign-up' ? 'Create your account' : 'Welcome back'}</h1>
    {commerce?.config?.clerkPublishableKey ? location.pathname === '/sign-up'
      ? <SignUp routing="hash" forceRedirectUrl={destination} signInUrl={`/sign-in?returnTo=${encodeURIComponent(destination)}`} />
      : <SignIn routing="hash" forceRedirectUrl={destination} signUpUrl={`/sign-up?returnTo=${encodeURIComponent(destination)}`} />
      : commerce?.localSignIn ? <><p>Development preview using a signed local identity.</p><button onClick={() => void localLogin()}>Sign in to local preview</button></>
        : <p role="status">{commerce?.status === 'loading' ? 'Loading sign-in…' : 'Account sign-in is currently unavailable.'}</p>}
    <p><a href="/terms">Terms of Use</a> · <a href="/privacy">Privacy Policy</a></p>{error && <p role="alert">{error}</p>}</section></PublicShell>;
}
